import React, { useState, useContext } from 'react';

import {
  Menu,
  MenuList,
  MenuButton,
  MenuItem,
  MenuDivider,
  Flex,
  useToast,
  MenuOptionGroup,
  MenuItemOption,
  IconButton,
  Tooltip,
  rem,
} from '@vimeo/bokeh';
import { ChevronRight, ChevronLeft, EllipsisH } from '@vimeo/bokeh-icons';
import { useCapability } from '@vimeo/core/hooks/use-capability';
import { translate } from '@vimeo/i18n-runtime';
import { buildUpgradePlanUrl } from '@vimeo/picox';
import { Viewer, ViewerContext } from '@vimeo/viewer';

import { setLanguage, Locales } from '../../setLanguage';
import DoNotSellLink from '../DoNotSellLink';
import { getLegalLinks } from '../Links';
import { MatureContentModal } from '../MatureContentModal';
import { MadeWithLoveAnimated } from './MadeWithLoveAnimated';

const MENU_WIDTH = '255px';

type SelectedMenu = 'main' | 'language' | 'legal';

interface Props {
  children?: React.ReactNode;
  impressumQualifies?: boolean;
  locale: string;
  locales?: Locales[];
  localeLabels?: string[];
  token: string;
  contentViewingPrefs: Viewer['contentViewingPrefs'];
  isDoNotSellReady?: boolean;
  onMenuChange?: (menu: SelectedMenu) => void;
  onOpenMatureContentFilter?: () => void;
  customMenuItem?: React.ReactNode;
}

const MenuItemWithChevronRight = ({ children }: { children: React.ReactNode }) => (
  <Flex width="100%" alignItems="center" justifyContent="space-between">
    {children}
    <ChevronRight />
  </Flex>
);

const MenuItemWithChevronLeft = ({ children }: { children: React.ReactNode }) => (
  <Flex width="100%" alignItems="center" gap="sm">
    <ChevronLeft />
    {children}
  </Flex>
);

export const EssentialFooterMenu = ({
  children,
  impressumQualifies,
  locale,
  locales = [],
  localeLabels = [],
  token,
  contentViewingPrefs,
  isDoNotSellReady,
  onMenuChange,
  onOpenMatureContentFilter,
  customMenuItem,
}: Props) => {
  const errorToast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<SelectedMenu>('main');

  const viewer = useContext(ViewerContext);
  const isSimplifiedSite = viewer?.isSimplifiedSite;

  const user = viewer?.user;
  const teamUser = viewer?.teamUser;

  const {
    capabilities: { hasEnterprise: ownerHasEnterprise },
    loading: loadingOwnerCapability,
  } = useCapability(['hasEnterprise'], teamUser?.ownerId);

  const {
    capabilities: { canUpgrade },
    loading: loadingCapability,
  } = useCapability(['canUpgrade'], user?.id);

  const shouldShowUpgrade =
    !isSimplifiedSite &&
    !(loadingCapability || loadingOwnerCapability) &&
    canUpgrade &&
    !ownerHasEnterprise;

  const onToggleMenu = (menu: SelectedMenu) => {
    setSelectedMenu(menu);
    onMenuChange?.(menu);
  };

  const onLanguageClick = (locale: Locales) => {
    setLanguage({ token, locale }).then((response: Response) => {
      if (!response.ok) {
        errorToast({
          title: translate('Sorry, something went wrong. Please try again.'),
          variant: 'warning',
        });
      } else {
        // Refresh the page to update the language choice
        window.location.reload();
      }
    });
  };

  const MainMenu = () => (
    <MenuList width={MENU_WIDTH}>
      {customMenuItem}
      <MenuItem onClick={() => onToggleMenu('language')}>
        <MenuItemWithChevronRight>{translate('Language')}</MenuItemWithChevronRight>
      </MenuItem>
      <MenuItem
        onClick={() => onToggleMenu('legal')}
        sx={{ '@media screen and (min-width: 480px)': { display: 'none' } }}
      >
        <MenuItemWithChevronRight>{translate('Legal')}</MenuItemWithChevronRight>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setShowModal(true);
          onOpenMatureContentFilter?.();
        }}
        closeOnSelect={true}
      >
        <MenuItemWithChevronRight>{translate('Mature content filter')}</MenuItemWithChevronRight>
      </MenuItem>
      {!isSimplifiedSite && (
        <MenuItem as="a" href="/help" target="_blank" rel="noreferrer">
          {translate('Help')}
        </MenuItem>
      )}
      {shouldShowUpgrade && (
        <MenuItem
          as="a"
          href={buildUpgradePlanUrl({
            paywallTrigger: 'footer_menu_upgrade_button',
            paywallLocation: 'footer_menu',
            paywallFeature: 'general',
          })}
          color="upsell-primary"
          target="_blank"
          rel="noreferrer"
          _hover={{
            color: 'upsell-primary',
            _dark: { color: 'upsell-primary' },
          }}
          _dark={{ color: 'upsell-primary' }}
        >
          {translate('Upgrade')}
        </MenuItem>
      )}
      <MenuDivider />
      <MadeWithLoveAnimated />
    </MenuList>
  );

  const LanguageMenu = () => (
    <MenuList width={MENU_WIDTH}>
      <MenuItem onClick={() => onToggleMenu('main')}>
        <MenuItemWithChevronLeft>{translate('Language')}</MenuItemWithChevronLeft>
      </MenuItem>
      <MenuOptionGroup
        defaultValue={locale}
        type="radio"
        onChange={(value) => onLanguageClick(value as Locales)}
      >
        {locales.map((currLocale, index) => (
          <MenuItemOption key={currLocale} value={currLocale}>
            {localeLabels[index]}
          </MenuItemOption>
        ))}
      </MenuOptionGroup>
    </MenuList>
  );

  const LegalMenu = () => (
    <MenuList width={MENU_WIDTH}>
      <MenuItem onClick={() => onToggleMenu('main')}>
        <MenuItemWithChevronLeft>{translate('Legal')}</MenuItemWithChevronLeft>
      </MenuItem>
      {getLegalLinks()
        .filter(
          (link) =>
            (impressumQualifies ? true : link.title !== 'Impressum') &&
            (!isSimplifiedSite || link.forSimplifiedSite === isSimplifiedSite),
        )
        .map((link) => (
          <MenuItem key={link.label} as="a" href={link.href} target="_blank" rel="noreferrer">
            {link.label}
          </MenuItem>
        ))}
      <DoNotSellLink footerType="essential" isDoNotSellReady={isDoNotSellReady} />
    </MenuList>
  );

  return (
    <Flex boxSize={rem(40)} alignItems="center" justifyContent="center">
      <Menu closeOnSelect={false} onClose={() => onToggleMenu('main')}>
        {children ? (
          children
        ) : (
          <Tooltip label={translate('Language & content')} placement="right">
            <MenuButton
              as={IconButton}
              icon={<EllipsisH />}
              variant="tertiary"
              size="sm"
              borderRadius="round"
              backgroundColor="transparent"
            />
          </Tooltip>
        )}
        {selectedMenu === 'main' && <MainMenu />}
        {selectedMenu === 'language' && <LanguageMenu />}
        {selectedMenu === 'legal' && <LegalMenu />}
      </Menu>
      <MatureContentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        token={token}
        {...contentViewingPrefs}
      />
    </Flex>
  );
};
